import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Sparkles, ArrowRight, Quote, Calendar, ChevronRight,
  Heart, Star, TrendingUp, Award
} from 'lucide-react';

const JourneyEvolution = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [evolution, setEvolution] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvolution = async () => {
      if (!token) return;
      
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${API}/journey/evolution`, { headers });
        setEvolution(response.data);
      } catch (error) {
        console.error('Error fetching evolution:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvolution();
  }, [token]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Ton Évolution">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!evolution?.has_data) {
    return (
      <DashboardLayout title="Ton Évolution">
        <div className="max-w-2xl mx-auto py-12 text-center">
          <div className="card-premium p-12">
            <TrendingUp className="text-[#D4AF37] mx-auto mb-4" size={48} />
            <h2 className="font-serif text-2xl text-white mb-4">
              Ton évolution commence ici
            </h2>
            <p className="text-[#A1A1AA] mb-8">
              Continue ton parcours pour voir ta transformation prendre forme.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-gold"
            >
              Continuer mon parcours
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Ton Évolution">
      <div className="max-w-3xl mx-auto py-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkles className="text-[#D4AF37]" size={20} />
            <span className="text-[#D4AF37] text-xs font-accent tracking-[0.25em]">TA TRANSFORMATION</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-white">
            Regarde ton évolution
          </h1>
        </motion.div>

        {/* Initial Expectations - AVANT */}
        {evolution.initial_expectations?.text && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-premium p-6 mb-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#52525B]/20 border border-[#52525B]/30">
                <Heart className="text-[#52525B]" size={20} />
              </div>
              <div>
                <p className="text-[#52525B] text-xs font-accent tracking-wider">AVANT</p>
                <p className="text-white font-medium">Tes attentes initiales</p>
              </div>
              {evolution.initial_expectations.date && (
                <span className="ml-auto text-[#52525B] text-xs flex items-center gap-1">
                  <Calendar size={12} />
                  {formatDate(evolution.initial_expectations.date)}
                </span>
              )}
            </div>
            
            <div className="pl-4 border-l-2 border-[#52525B]/30">
              <Quote className="text-[#52525B] mb-2" size={16} />
              <p className="text-[#A1A1AA] font-light italic leading-relaxed">
                "{evolution.initial_expectations.text}"
              </p>
            </div>
          </motion.div>
        )}

        {/* Step Reviews - PENDANT */}
        {evolution.step_reviews?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-gold p-6 mb-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                <TrendingUp className="text-[#D4AF37]" size={20} />
              </div>
              <div>
                <p className="text-[#D4AF37] text-xs font-accent tracking-wider">PENDANT</p>
                <p className="text-white font-medium">Ton parcours étape par étape</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {evolution.step_reviews.map((review, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="pl-4 border-l-2 border-[#D4AF37]/30 py-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <ChevronRight className="text-[#D4AF37]" size={14} />
                      <span className="text-[#E5C585] text-sm font-medium">
                        {review.step_name}
                      </span>
                    </div>
                    <span className="text-[#52525B] text-xs">
                      {formatDate(review.created_at)}
                    </span>
                  </div>
                  <p className="text-[#A1A1AA] font-light text-sm leading-relaxed ml-5">
                    "{review.review_text}"
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Completion - APRÈS */}
        {evolution.completion && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card-premium p-6 mb-6 border-[#FFD700]/30"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-[#FFD700]/10 border border-[#FFD700]/30">
                <Award className="text-[#FFD700]" size={20} />
              </div>
              <div>
                <p className="text-[#FFD700] text-xs font-accent tracking-wider">APRÈS</p>
                <p className="text-white font-medium">Ta transformation</p>
              </div>
              {evolution.completion.completed_at && (
                <span className="ml-auto text-[#52525B] text-xs flex items-center gap-1">
                  <Calendar size={12} />
                  {formatDate(evolution.completion.completed_at)}
                </span>
              )}
            </div>
            
            <div className="pl-4 border-l-2 border-[#FFD700]/30">
              <div className="mb-3">
                <span className="text-[#52525B] text-xs">Comment tu te sens :</span>
                <p className="text-[#E5C585] font-medium">{evolution.completion.feeling}</p>
              </div>
              <Quote className="text-[#FFD700] mb-2" size={16} />
              <p className="text-white font-light italic leading-relaxed">
                "{evolution.completion.final_thoughts}"
              </p>
            </div>
          </motion.div>
        )}

        {/* Summary Card */}
        {evolution.initial_expectations?.text && evolution.step_reviews?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-12 p-8 bg-gradient-to-b from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20"
          >
            <Star className="text-[#D4AF37] mx-auto mb-4" size={32} />
            <h3 className="font-serif text-xl text-white mb-2">
              Tu n'es plus la même
            </h3>
            <p className="text-[#A1A1AA] font-light mb-6">
              Chaque étape t'a rapprochée de la femme que tu deviens.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-gold"
            >
              Continuer à évoluer
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </motion.div>
        )}

        {/* CTA if not completed */}
        {!evolution.journey_completed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center mt-8"
          >
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-[#D4AF37]/30 text-[#E5C585] hover:bg-[#D4AF37]/10"
            >
              Retour au tableau de bord
            </Button>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default JourneyEvolution;
